
import {
  Component, Prop, Mixins,
} from 'vue-property-decorator';
import { fetchSupportedServiceLevels } from '@/features/shipment/services/shipment.services';
import { CarrierFilterType } from '@/features/common/raw';
import { deepClone, showErrorAlertDialog, showSuccessAlertDialog } from '@/features/common/helpers';
import focusMixin from '@/features/common/mixins/focusMixin';
import { patchPreferences } from '@/features/settings/services/preferences.services';
import { UserPreferences } from '@/features/settings/types';
import FilterItem from './FilterItem.vue';

export interface ServiceToFilter {
  name: string,
  show: boolean,
  levels: Array<{name: string, show: boolean}>,
}

@Component({
  components: {
    FilterItem,
  },
})
export default class FiltersModal extends Mixins(focusMixin) {
  @Prop({ default: false }) showModal!: boolean;

  private weightUnitsItems: Array<string> = ['Lbs', 'Kg'];

  private sortItems = [
    {
      text: this.$t('labels.sort-to-high', { name: 'Price' }),
      value: 'price-to-high',
    },
    {
      text: this.$t('labels.sort-to-low', { name: 'Price' }),
      value: 'price-to-low',
    },
    {
      text: this.$t('labels.sort-date-earliest-delivery'),
      value: 'date-to-high',
    },
    {
      text: this.$t('labels.sort-date-latest-delivery'),
      value: 'date-to-low',
    },
  ]

  private sortBy: 'price-to-high' | 'price-to-low' | 'date-to-low' | 'date-to-high' = 'price-to-low';

  private initSortBy = this.sortBy;

  private serviceLevels: CarrierFilterType[] = [];

  private totalServicesCount = 0;

  private lastCarriersToFilter: CarrierFilterType[] = [];

  private carriersToFilter: CarrierFilterType[] = [];

  private isLoadingSettingsSync = false;

  get show(): boolean {
    return this.showModal;
  }

  set show(value: boolean) {
    this.$emit('closed');

    this.carriersToFilter = deepClone(this.lastCarriersToFilter);
  }

  get userPreferences(): UserPreferences | null {
    return this.$store.getters.getUserPreferences;
  }

  get carrierFiltersFromPref(): string[] | null {
    return this.userPreferences ? this.userPreferences.carrierFilters : null;
  }

  get selectedServicesCount(): number {
    let count = 0;
    this.carriersToFilter.forEach((carrier) => {
      if (carrier.name !== 'TotalShip' && carrier.show === true) {
        count += carrier.levels.filter((level) => level.show === true).length;
      }
    });
    return count;
  }

  get selectedCarriersCount(): number {
    return this.carriersToFilter
      .filter((item) => item.name !== 'TotalShip' && item.show === true).length;
  }

  get isSelectAllOptions(): boolean {
    const filterable = this.carriersToFilter.filter((item) => item.name !== 'TotalShip');
    return filterable.length > 0 && this.selectedCarriersCount === filterable.length;
  }

  set isSelectAllOptions(value: boolean) {
    this.carriersToFilter.forEach((el) => this.toggleService({ name: el.name, value }));
  }

  async created(): Promise<void> {
    await this.populateSupportedServiceLevels();
    this.prepareFilter();
  }

  private async populateSupportedServiceLevels() {
    const levels = await fetchSupportedServiceLevels();
    this.totalServicesCount = levels.length;

    const servicesWithLevels: CarrierFilterType[] = [];

    levels.forEach((level) => {
      const carrierCode = servicesWithLevels.find((s) => s.name === level.carrierCode);

      if (!carrierCode) {
        servicesWithLevels.push({
          name: level.carrierCode,
          show: false,
          levels: [],
        });
      }

      for (const service of servicesWithLevels) {
        if (service.name === level.carrierCode) {
          service.levels.push({
            name: level.displayName,
            value: level.selectedService,
            show: false,
          });
          break;
        }
      }
    });

    this.serviceLevels = servicesWithLevels;
  }

  private async prepareFilter() {
    const clonedCarrierFilterItems = [...this.serviceLevels];
    const carrierFiltersPrefLength = this.carrierFiltersFromPref?.length;

    for (let i = 0; i < clonedCarrierFilterItems.length; i += 1) {
      let isShow = !carrierFiltersPrefLength;
      for (let j = 0; j < clonedCarrierFilterItems[i].levels.length; j += 1) {
        if (!carrierFiltersPrefLength || this.carrierFiltersFromPref?.includes(
          clonedCarrierFilterItems[i].levels[j].value,
        )) {
          clonedCarrierFilterItems[i].levels[j].show = true;
          isShow = true;
        }
      }
      clonedCarrierFilterItems[i].show = isShow;
    }

    this.carriersToFilter = [...clonedCarrierFilterItems];
    this.updateLastCarriersToFilter();
  }

  private toggleService({ value, name }): void {
    const serviceToFilter = this.carriersToFilter.find((el) => el.name === name);
    if (serviceToFilter) {
      serviceToFilter.show = value;
      serviceToFilter.levels = serviceToFilter.levels.map((el) => {
        const clone = { ...el };
        clone.show = value;
        return clone;
      });
    }
  }

  private toggleLevel({ value, serviceName, selectedService }): void {
    const serviceToFilter = this.carriersToFilter.find((el) => el.name === serviceName);
    const levelToFilter = serviceToFilter?.levels.find((el) => el.value === selectedService);

    if (serviceToFilter && levelToFilter) {
      levelToFilter.show = value;
      const showService = serviceToFilter?.levels.find((el) => el.show);

      if (!showService) {
        serviceToFilter.show = false;
      }
    }
  }

  private applyFilters(): void {
    this.updateLastCarriersToFilter();
    this.$emit('applyFilters', this.carriersToFilter);

    if (this.sortBy !== this.initSortBy) {
      this.$emit('sort', this.sortBy);
      this.initSortBy = this.sortBy;
    }
  }

  private updateLastCarriersToFilter(): void {
    this.lastCarriersToFilter = deepClone(this.carriersToFilter);
  }

  private closeModal(): void {
    this.$emit('closed');
    this.carriersToFilter = deepClone(this.lastCarriersToFilter);
  }

  private async setFiltersAsNewDefault() {
    if (!this.userPreferences) return;

    this.isLoadingSettingsSync = true;

    const carriersLevels: string[] = [];
    for (let i = 0; i < this.carriersToFilter.length; i += 1) {
      for (let j = 0; j < this.carriersToFilter[i].levels.length; j += 1) {
        if (this.carriersToFilter[i].levels[j].show) {
          carriersLevels.push(this.carriersToFilter[i].levels[j].value);
        }
      }
    }

    this.$store.commit('patchUserPreferences', {
      key: 'carrierFilters',
      value: carriersLevels,
    });

    const response = await patchPreferences(this.userPreferences.id, [
      {
        path: 'carrierFilters',
        op: 'replace',
        value: carriersLevels,
      },
    ]);

    this.isLoadingSettingsSync = false;

    if (!response) {
      showErrorAlertDialog();
      return;
    }

    showSuccessAlertDialog(this.$t('shipment.alertDialog.filtersAddedAsDefault') as string);
    this.updateLastCarriersToFilter();
  }
}
